import {
  Button,
  Toast,
  ToastBody,
  Toaster,
  ToastFooter,
  ToastIntent,
  ToastTitle,
  ToastTrigger,
  useId,
  useToastController,
} from '@fluentui/react-components';
import React, { createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';

type ToastOptions = Parameters<ReturnType<typeof useToastController>['dispatchToast']>[1];

export interface IToastContext {
  showSuccessToast: (message: string, options?: Omit<ToastOptions, 'intent'>) => void;
  showErrorToast: (message: string | undefined | null, traceId: string | undefined | null, timestamp: string | undefined | null, options?: Omit<ToastOptions, 'intent'>) => void;
  showWarningToast: (message: string, options?: Omit<ToastOptions, 'intent'>) => void;
  showInfoToast: (message: string, options?: Omit<ToastOptions, 'intent'>) => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const ToastContext = createContext<IToastContext>({} as IToastContext);

// eslint-disable-next-line react-refresh/only-export-components
export const useToastContext = (): IToastContext => {
  return useContext(ToastContext);
};

interface IToastContextProviderProps {
  children: React.ReactNode;
}

const LIMIT_OF_TOASTS = 10;
const DEFAULT_TOAST_DISPLAY_DURATION_IN_SEC = 10 * 1000;

export function ToastContextProvider({ children }: IToastContextProviderProps) {
  const toasterId = useId('toaster');
  const { dispatchToast } = useToastController(toasterId);
  const { t } = useTranslation('', { keyPrefix: 'toast' });

  async function copyToClipboard(text: string): Promise<void> {
    await navigator.clipboard.writeText(text);
  }

  function extractText(node: React.ReactNode): string {
    const parts: string[] = [];

    const walk = (n: React.ReactNode): void => {
      if (n === null || n === undefined || typeof n === 'boolean') {
        return;
      }
      if (typeof n === 'string' || typeof n === 'number') {
        const text = String(n).trim();
        if (text) {
          parts.push(text);
        }
        return;
      }
      if (Array.isArray(n)) {
        n.forEach(walk);
        return;
      }
      if (React.isValidElement(n)) {
        walk((n.props as { children?: React.ReactNode }).children);
      }
    };

    walk(node);
    return parts.join('\n');
  }

  function showToast(message: React.ReactNode, intent: ToastIntent, options?: ToastOptions): void {
    // const truncatedMessage = truncateText(message);

    const title = getToastTitleByIntent(intent);
    const mergedOptions = mergeOptions(intent, options);
    dispatchToast(
      <Toast>
        <ToastTitle
          action={
            <ToastTrigger>
              <Button appearance="transparent">{t('dismiss')}</Button>
            </ToastTrigger>
          }
        >
          {title}
        </ToastTitle>
        <ToastBody style={{ gridColumn: '2 / span 3' }}>{message}</ToastBody>
        <ToastFooter style={{ gridColumn: '2 / span 3' }}>
          <Button appearance="transparent" onClick={() => copyToClipboard(extractText(message))}>
            {t('copy')}
          </Button>
        </ToastFooter>
      </Toast>,
      mergedOptions,
    );
  }

  function mergeOptions(intent: ToastIntent, options?: ToastOptions): ToastOptions {
    return {
      ...options,
      timeout: options?.timeout || DEFAULT_TOAST_DISPLAY_DURATION_IN_SEC,
      intent,
    };
  }

  function getToastTitleByIntent(intent: ToastIntent): string {
    switch (intent) {
      case 'success':
        return t('success');
      case 'warning':
        return t('warning');
      case 'error':
        return t('error');
      case 'info':
      default:
        return t('info');
    }
  }

  function renderErrorToastBody(message: string | undefined | null, traceId: string | undefined | null, timestamp: string | undefined | null): React.ReactNode {
    const hasDetails = Boolean(traceId || timestamp);

    return (
      <div style={{ display: 'grid', gap: '0.25rem', lineHeight: 1.4 }}>
        {message ? <div>{message}</div> : <div>{t('unknownError')}</div>}

        {hasDetails ? (
          <div style={{ display: 'grid', gap: '0.5rem', marginTop: '0.25rem' }}>
            {traceId ? (
              <div style={{ display: 'grid', gap: '0.125rem' }}>
                <span style={{ fontWeight: 600 }}>{t('traceId')}:</span>
                <span style={{ wordBreak: 'break-all' }}>{traceId}</span>
              </div>
            ) : null}
            {timestamp ? (
              <div style={{ display: 'grid', gap: '0.125rem' }}>
                <span style={{ fontWeight: 600 }}>{t('timestamp')}:</span>
                <span>{timestamp}</span>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  }

  function showSuccessToast(message: string, options?: Omit<ToastOptions, 'intent'>): void {
    showToast(message, 'success', options);
  }

  function showErrorToast(message: string | undefined | null, traceId: string | undefined | null, timestamp: string | undefined | null, options?: Omit<ToastOptions, 'intent'>): void {
    showToast(renderErrorToastBody(message, traceId, timestamp), 'error', options);
  }

  function showWarningToast(message: string, options?: Omit<ToastOptions, 'intent'>): void {
    showToast(message, 'warning', options);
  }

  function showInfoToast(message: string, options?: Omit<ToastOptions, 'intent'>): void {
    showToast(message, 'info', options);
  }

  return (
    <ToastContext.Provider
      value={{
        showSuccessToast,
        showErrorToast,
        showWarningToast,
        showInfoToast,
      }}
    >
      <Toaster toasterId={toasterId} limit={LIMIT_OF_TOASTS} />
      {children}
    </ToastContext.Provider>
  );
}
